<template>
	<v-container
		class="brightBackground"
		fluid
		style="min-height: 100%"
		width="100%"
	>
		
		<v-container fluid style="max-width: 1100px;" class="pt-xs-0 pt-sm-0 pt-md-0 pt-lg-6">

			<v-row>
				<v-col class="pt-0 pa-0 ma-0">
					<v-card v-if="tabItems.length > 0" class="mb-3">

						<!-- -------------------------------------------- -->
						<!--   Tab menu -->
						<!-- -------------------------------------------- -->
						<v-tabs
							v-model="tabActive"
							background-color="secondary accent-4"
							center-active
							dark
							show-arrows
						>

							<!-- Tab button -->
							<v-tab
								v-for="item in tabItems"
								:key="item.id"
								style="text-transform: none;"
							>
								<div class="mr-1">{{ item.name }}</div>

								<!-- Tab close button -->
								<v-btn
									class="ml-1 mr-n2"
									right
									icon
									x-small
									@click.stop="removeTabItemAndListenerById(item.id)"
									color="grey lighten-1"
								>
									<v-icon>mdi-close</v-icon>
								</v-btn>
							</v-tab>
							
						</v-tabs>
					
					</v-card>


					<!-- -------------------------------------------- -->
					<!--   Tab content container -->
					<!-- -------------------------------------------- -->

					<!-- I am NOT using Vuetifys tab-items, they have very buggy behaviour when opening new tabs. Better just use v-cards and v-show="" -->
					<v-card v-for="item in tabItems" :key="item.id"
						v-show="(tabActiveId === item.id)"
						color="boxOnBrightBackground"
						flat
						class="pa-3 mb-4"
					>
						<!-- Tab component - Either a simple subfolder edit, or a bigger View Template Editor -->
						<keep-alive>
							<component :id="item.id" :name="item.name" :document-type="item.documentType" :doc-data="item.docData" :is="item.component" />
						</keep-alive>
					</v-card>
					

					
					
				</v-col>
			</v-row>
			
		</v-container>

	</v-container>
</template>



<script>

// ----------------------------------------------------
//
//  Main script
//
// ----------------------------------------------------

import agile from '@/shift/agile';
import fb from '@/shift/fb';
import { AiEventBus } from '@/main';


export default
{
	name: 'ViewsEditorTabMenu',
	
	
	// ----------------------------------
	//	VUE: Data and computed
	// ----------------------------------

	data: () =>
	({
		tabActive: null,
		tabItems: [],

		realtimeDataListeners: [],

		initialSyncUserTab: null,
		waitingForInitialSyncToRest: true,
		hasInitializedUserSync: false,
		allowUserSync: true,
	}),

	computed:
	{
		agile()
		{
			return agile;
		},

		routeKey()
		{
			return this.$route.params.key;
		},

		tabActiveId()
		{
			if (this.tabItems[this.tabActive]) // Not always the case
			{
				return this.tabItems[this.tabActive].id;
			}
			else
			{
				return this.tabItems[0].id;
			}
			
		}
	},


	// ----------------------------------
	//	VUE: Watching things
	// ----------------------------------

	watch:
	{
		// ----------------------------------------------------
		//
		//  Send user state of active tabs to firestore
		//
		// ----------------------------------------------------

		tabActive()
		{
			
			// We want to save which documents we have open in the tab menu, and the active tab
			if (agile.loggedInUser.uid && this.hasInitializedUserSync && this.allowUserSync)
			{
				// We don't want to spam the firestore database, wait atleast 1 second before we update, and block other requests to sync meanwhile
				this.allowUserSync = false;
				const scopeThis = this;

				setTimeout(function()
				{
					scopeThis.allowUserSync = true;

					// Create array with open tabs id's
					const openTabs = [];
					for (let i = 0; i < scopeThis.tabItems.length; i++)
					{
						openTabs.push( { id: scopeThis.tabItems[i].id, itemType: scopeThis.tabItems[i].itemType } );
					}
					const activeTabId = (scopeThis.tabItems.length > 0) ? scopeThis.tabItems[scopeThis.tabActive].id : null ;

					

					const saveStateData =
					{
						viewsEditorActiveTab: activeTabId,
						viewsEditorOpenTabs: openTabs
					};
					

					// Ok, do the sync command
					fb.db.collection("users").doc(agile.loggedInUser.uid).collection("saved-states").doc(scopeThis.routeKey).set(saveStateData, { merge: true })
					.then(() =>
					{
						//console.log("Updated user tab states", scopeThis.treeOpenNodes);
					})
					.catch((error) =>
					{
						console.log('Error updating active tabs state:', error.message);
					});

				}, 1000);

				
			}
		}
	},



	// ----------------------------------------------------
	//
	//  VUE: Create and Mount
	//
	// ----------------------------------------------------

	created()
	{
		AiEventBus.$on('viewTemplatesOpenFolder', this.openSortingFolderEditorWithLiveListener);
		AiEventBus.$on('viewTemplatesOpenTemplate', this.openViewEditorWithLiveListener);
	},

	mounted()
	{
		const scopeThis = this;

		// This is to give the connection a chance to set the active tab to the users last active tab
		// The code for this is far from fool proof, but on fast connections, the page should usually land in the last document he edited.
		setTimeout(function()
		{
			scopeThis.waitingForInitialSyncToRest = false;
		}, 2000);
		
	},


	// --------------------------------------------------------------------
	//
	//	VUE: Methods
	//
	// --------------------------------------------------------------------

	methods:
	{


		// ----------------------------------
		//	Update open tab item with live data
		// ----------------------------------

		updateTabItem(docData)
		{
			for (let i = 0; i < this.tabItems.length; i++)
			{
				if (this.tabItems[i].id === docData.id) this.tabItems[i].docData = docData;
			}
		},


		// --------------------------------------------------------------------
		//
		//	Sync tabs from when the user last worked (called from ViewsEditor.vue)
		//
		// --------------------------------------------------------------------

		syncInitialTabs(activeTab, openTabs)
		{
			if (openTabs && openTabs.length > 0)
			{
				for (let i = 0; i < openTabs.length; i++)
				{
					if (openTabs[i].itemType === 'sortingFolder')
					{
						this.openSortingFolderEditorWithLiveListener(openTabs[i].id);
					}
					else if (openTabs[i].itemType === 'viewTemplate')
					{
						this.openViewEditorWithLiveListener(openTabs[i].id);
					}
				}

				this.initialSyncUserTab = activeTab;
				
			}
		},


		
		// --------------------------------------------------------------------
		//
		//	Open a sorting folder document
		//
		// --------------------------------------------------------------------

		openSortingFolderEditorWithLiveListener(id)
		{
			for (let i = 0; i < this.tabItems.length; i++)
			{
				if (this.tabItems[i].id == id)
				{
					// Tab is already open, focus on it
					console.log("* Tab already open");

					// Stop further progress in this function
					return;
				}
			}
			
			
			// ----------------------------------
			//	Firestore
			// ----------------------------------
			
			const doc = fb.db
				.collection('dev-collection')
				.doc('dev-doc')
				.collection(this.routeKey)
				.doc(this.routeKey)
				.collection('views-editor-sorting-folders')
				.doc(id);

			
			// Create firestore listener
			const realTimeUnsub = doc.onSnapshot(docSnapshot =>
			{
				const original = this.getTabItemById(docSnapshot.id);

				if (original === null)
				{
					// ------------------------------------------------
					//	Open the document
					// ------------------------------------------------

					if (docSnapshot.exists)
					{
						const docData = docSnapshot.data();
						
						this.tabItems.push(
						{
							id: docSnapshot.id,
							name: docData.name,
							itemTypeName: 'Sorting folder',
							itemType: 'sortingFolder',
							component: 'views-editor-tab-item-folder',
							documentType: 'sorting-folder-views',
							docData: docData,
						});


						if (this.waitingForInitialSyncToRest)
						{
							if (this.initialSyncUserTab === docSnapshot.id) this.tabActive = this.tabItems.length-1;
						}
						else
						{
							this.tabActive = this.tabItems.length-1;
						}
					}
					else
					{
						// Query went to document that does not exist. Shut down.
						this.removeRealtimeListenerById(id);
					}
				}
				else
				{
					// ------------------------------------------------
					//	Modify the document
					// ------------------------------------------------

					if (docSnapshot.exists)
					{
						const docData = docSnapshot.data();
						original.name = docData.name;
					}
					else
					{
						// If document does not exist any more, then it must have been deleted
						// Remove listener and remove from tab menu
						this.removeTabItemAndListenerById(id);
						
						this.$root.ai_app.snackbarText = `'${original.name}' was deleted`;
						this.$root.ai_app.snackbarTimeout = 4000;
						this.$root.ai_app.snackbar = true;
					}

				}
				
			},
			er =>
			{
				this.removeRealtimeListenerById(id)
				this.$root.$alert("Error", er.message);
			});
			

			this.realtimeDataListeners.push( { id: id, unsubListener:realTimeUnsub });
			
		},



		// --------------------------------------------------------------------
		//
		//	Open View template
		//
		// --------------------------------------------------------------------
		
		openViewEditorWithLiveListener(id)
		{
			for (let i = 0; i < this.tabItems.length; i++)
			{
				if (this.tabItems[i].id == id)
				{
					// Tab is already open, focus on it
					console.log("* Tab already open");

					// Stop further progress in this function
					return;
				}
			}
			

			// ----------------------------------
			//	Firestore
			// ----------------------------------
			
			const doc = fb.db
				.collection('dev-collection')
				.doc('dev-doc')
				.collection(this.routeKey)
				.doc(this.routeKey)
				.collection('view-templates')
				.doc(id);

			
			// Create firestore listener
			const realTimeUnsub = doc.onSnapshot(docSnapshot =>
			{
				const original = this.getTabItemById(docSnapshot.id);

				if (original === null)
				{
					// ------------------------------------------------
					//	Open the document and keep listening
					// ------------------------------------------------

					if (docSnapshot.exists)
					{
						const docData = docSnapshot.data();
						
						this.tabItems.push(
						{
							id: docSnapshot.id,
							name: docData.name,
							itemTypeName: 'View template',
							itemType: 'viewTemplate',
							component: 'views-edit-template',
							docData: docData,
						});

						if (this.waitingForInitialSyncToRest)
						{
							if (this.initialSyncUserTab === docSnapshot.id) this.tabActive = this.tabItems.length-1;
						}
						else
						{
							this.tabActive = this.tabItems.length-1;
						}
					}
					else
					{
						// Query went to document that does not exist. Shut down.
						this.removeRealtimeListenerById(id);
					}
				}
				else
				{
					// ------------------------------------------------
					//	Modify the document
					// ------------------------------------------------

					if (docSnapshot.exists)
					{
						const docData = docSnapshot.data();
						original.name = docData.name;
					}
					else
					{
						// If document does not exist any more, then it must have been deleted
						// Remove listener and remove from tab menu
						this.removeTabItemAndListenerById(id);
						
						this.$root.ai_app.snackbarText = `'${original.name}' was deleted`;
						this.$root.ai_app.snackbarTimeout = 4000;
						this.$root.ai_app.snackbar = true;
					}

				}
				
			},
			er =>
			{
				this.removeRealtimeListenerById(id)
				this.$root.$alert("Error", er.message);
			});
			

			this.realtimeDataListeners.push( { id: id, unsubListener:realTimeUnsub });
			
		},



		// --------------------------------------------------------------------
		//
		//	Utility functions
		//
		// --------------------------------------------------------------------

		
		getTabItemById(id)
		{
			for (let i = 0; i < this.tabItems.length; i++)
			{
				if (this.tabItems[i].id === id)
				{
					return this.tabItems[i];
				}
			}
			return null;
		},

		removeTabItemAndListenerById(id)
		{
			this.removeTabItemById(id);
			this.removeRealtimeListenerById(id);
		},

		removeTabItemById(id)
		{
			for (let i = 0; i < this.tabItems.length; i++)
			{
				if (this.tabItems[i].id === id)
				{
					this.tabItems.splice(i, 1);
					return;
				}
			}
		},


		removeRealtimeListenerById(id)
		{
			for (let i = 0; i < this.realtimeDataListeners.length; i++)
			{
				if (this.realtimeDataListeners[i].id === id)
				{
					this.realtimeDataListeners[i].unsubListener();
				}
			}
		},
		
		
	},


	// --------------------------------------------------------------------
	//
	//	VUE: Destroy event hook
	//
	// --------------------------------------------------------------------

	beforeDestroy()
	{
		// Close down live listeners
		//console.log("* Unsubscribing live listeners for View templates editing opended");
		
		for (let i = 0; i < this.realtimeDataListeners.length; i++)
		{
			this.realtimeDataListeners[i].unsubListener();
		}
		
		AiEventBus.$off('viewTemplatesOpenFolder', this.openSortingFolderEditorWithLiveListener);
		AiEventBus.$off('viewTemplatesOpenTemplate', this.openViewEditorWithLiveListener);
		
	},
    
}
</script>